/**
 * MAP VIEW PAGE
 * Full-screen Google Map with venue markers
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { venuesAPI, roadsAPI } from '../api';
import { BottomNav } from './Home';
import CopperRoadMap from '../components/Map';
import LoadingSpinner from '../components/LoadingSpinner';
import VenueModal from '../components/VenueModal';
import QRScanner from '../components/QRScanner';

export default function MapView() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const from = queryParams.get('from');

    const [venues, setVenues] = useState([]);
    const [roads, setRoads] = useState([]);
    const [selectedRoad, setSelectedRoad] = useState(null);
    const [checkins, setCheckins] = useState([]);
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [showScanner, setShowScanner] = useState(false);
    const [checkinResult, setCheckinResult] = useState(null);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([roadsAPI.getAll(), venuesAPI.getUserCheckins()])
            .then(async ([roadsRes, checkinsRes]) => {
                setRoads(roadsRes.data);
                setCheckins(checkinsRes.data);
                if (roadsRes.data.length > 0) {
                    const savedSlug = localStorage.getItem('selectedRoadSlug');
                    const roadToSelect = savedSlug 
                        ? roadsRes.data.find(r => r.slug === savedSlug) || roadsRes.data[0]
                        : roadsRes.data[0];
                    
                    setSelectedRoad(roadToSelect);
                    // CRITICAL: Await venues before finishing loading to ensure map can center correctly
                    const vRes = await venuesAPI.getAll(roadToSelect.slug);
                    setVenues(vRes.data);
                }
            })
            .finally(() => setLoading(false));
    }, []);

    const switchRoad = (road) => {
        setSelectedRoad(road);
        localStorage.setItem('selectedRoadSlug', road.slug);
        venuesAPI.getAll(road.slug).then(r => setVenues(r.data));
    };

    const checkedInIds = checkins.map(c => c.venue_id);

    const handleScanSuccess = async (token) => {
        setShowScanner(false);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const res = await venuesAPI.submitCheckin(token, position.coords.latitude, position.coords.longitude);
                    setCheckinResult({ success: true, message: res.data.message });
                    venuesAPI.getUserCheckins().then(r => setCheckins(r.data));
                } catch (err) {
                    setCheckinResult({ success: false, message: err.response?.data?.message || 'Check-in failed' });
                }
            },
            () => setCheckinResult({ success: false, message: 'Location access required. Enable location services and try again.' }),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div style={styles.screen}>
            {/* Header */}
            <div style={styles.header}>
                <div>
                    <div style={styles.headerTitle}>Copper Road Experiences Map</div>
                    <div style={{ color: '#7A8F96', fontSize: 11, fontWeight: '700', letterSpacing: 1, marginTop: 4 }}>
                        {selectedRoad?.name ? selectedRoad.name : <span style={{ fontSize: 16 }}>Loading...</span>}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={styles.toggleGroup}>
                        <button style={styles.toggleBtn} onClick={() => navigate(from === 'partners' ? '/partners' : '/trail')}>≡ List</button>
                        <button style={{ ...styles.toggleBtn, ...styles.toggleActive }}>🗺️ Map</button>
                    </div>
                    <div
                        onClick={() => window.location.href = '/profile'}
                        style={{
                            width: 38, height: 38, borderRadius: '50%',
                            background: '#ffffff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                            cursor: 'pointer', border: '2px solid #c48f4a',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            transition: 'transform 0.2s',
                        }}
                    >
                        <span style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))' }}>👤</span>
                    </div>
                </div>
            </div>

            {/* Road Selector */}
            {roads.length > 1 && (
                <div style={styles.roadTabs}>
                    {roads.map(r => (
                        <button
                            key={r.slug}
                            style={{ ...styles.roadTab, ...(selectedRoad?.slug === r.slug ? styles.roadTabActive : {}) }}
                            onClick={() => switchRoad(r)}
                        >
                            {r.region}
                        </button>
                    ))}
                </div>
            )}

            {/* Map */}
            <div style={styles.mapContainer}>
                <CopperRoadMap
                    venues={venues}
                    checkedInVenueIds={checkedInIds}
                    onVenueSelect={setSelectedVenue}
                />
            </div>

            {/* Venue Modal */}
            {selectedVenue && (
                <VenueModal
                    venue={selectedVenue}
                    isCheckedIn={checkedInIds.includes(selectedVenue.id)}
                    checkinAt={checkins.find(c => c.venue_id === selectedVenue.id)?.checkin_at}
                    onClose={() => setSelectedVenue(null)}
                    onScanQR={() => { setSelectedVenue(null); setShowScanner(true); }}
                />
            )}

            {showScanner && (
                <div style={styles.scannerModal}>
                    <QRScanner onScanSuccess={handleScanSuccess} onClose={() => setShowScanner(false)} />
                </div>
            )}

            {checkinResult && (
                <div style={{ ...styles.toast, background: checkinResult.success ? '#2e7d32' : '#c62828' }}>
                    {checkinResult.success ? '🏅 ' : '⚠️ '}{checkinResult.message}
                    <button style={styles.toastClose} onClick={() => setCheckinResult(null)}>✕</button>
                </div>
            )}

            <BottomNav active="/map" />
        </div>
    );
}

const styles = {
    screen: { display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'DM Sans, serif', background: '#F0EDE8' },
    header: { background: '#0F1A1F', padding: '48px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexShrink: 0 },
    headerTitle: { color: '#F0EDE8', fontSize: 22, fontWeight: 'bold', fontFamily: 'Playfair Display, serif' },
    toggleGroup: { display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: 20, padding: 3 },
    toggleBtn: { background: 'none', border: 'none', color: '#fff', fontSize: 12, padding: '6px 12px', borderRadius: 16, cursor: 'pointer', fontFamily: 'DM Sans, serif' },
    toggleActive: { background: '#fff', color: '#1a2234', fontWeight: 'bold' },
    scanBtn: { background: '#B87333', color: '#fff', border: 'none', borderRadius: 20, padding: '8px 16px', fontSize: 13, fontWeight: 'bold', cursor: 'pointer', fontFamily: 'DM Sans, serif' },
    roadTabs: { display: 'flex', gap: 8, padding: '10px 20px', background: '#0F1A1F', overflowX: 'auto', flexShrink: 0 },
    roadTab: { padding: '6px 14px', borderRadius: 20, border: '1px solid #B87333', background: 'none', color: '#B87333', fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, serif', whiteSpace: 'nowrap' },
    roadTabActive: { background: '#B87333', color: '#fff' },
    mapContainer: { flex: 1, position: 'relative' },
    scannerModal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 70, zIndex: 3000, background: '#000' },
    toast: { position: 'fixed', bottom: 90, left: 20, right: 20, borderRadius: 12, padding: '14px 40px 14px 16px', color: '#fff', zIndex: 999, fontSize: 14, fontWeight: 'bold' },
    toastClose: { position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer' }
};
